import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useRestApi } from "@/context/api";
import { useCurrentCompany } from "@/context/auth";
import { SecurityLogStatus, SecurityLogView, SecurityOperation } from "@/types/auth";

import { queryKeys } from "./queryKeys";

export interface SecurityLogsQueryParams {
  page: number;
  size: number;
  view?: SecurityLogView;
  operations?: SecurityOperation[];
  statuses?: SecurityLogStatus[];
  from?: string;
  userId?: string;
}

export const useSecurityLogsQuery = (queryParams: SecurityLogsQueryParams) => {
  const { getSecurityLogs } = useRestApi().securityLogs;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.SECURITY_LOGS, currentCompany.id, queryParams],
    queryFn: () => getSecurityLogs(currentCompany.id, queryParams),
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
};

export const useUserSecurityLogsInfiniteQuery = (userId: string, enabled: boolean) => {
  const { getUserSecurityLogs } = useRestApi().securityLogs;
  const currentCompany = useCurrentCompany();

  return useInfiniteQuery({
    queryKey: [queryKeys.USER_SECURITY_LOGS, currentCompany.id, userId],
    queryFn: ({ pageParam }) =>
      getUserSecurityLogs(currentCompany.id, userId, {
        size: 20,
        before: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || lastPage.items.length === 0) {
        return null;
      }
      return lastPage.items[lastPage.items.length - 1].eventTimestamp;
    },
    enabled,
    staleTime: 30000,
  });
};
